import { css } from '@emotion/css';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useLocation } from 'react-router-dom';
import { RootState } from '../../features';
import { actions } from '../../features/bestFriends';
import { throttle } from '../../lib/utils';
import { RefreshButton } from '../shared/buttons/RefreshButton';
import { reveal } from '../shared/keyframe-animations';
import { Loading } from '../shared/Loading';
import { LoadingFailed } from '../shared/LoadingFailed';
import { DialogRoute } from '../shared/routes/DialogRoute';
import { TitleHeader } from '../shared/TitleHeader';
import { BestFriendReportForm } from './BestFriendReportForm';
import { BestFriendsContent } from './BestFriendsContent';
import { BestFriendsEmpty } from './BestFriendsEmpty';
import { BestFriendsHintDialog } from './BestFriendsHintDialog';
import { BlockFailedDialog } from './BlockFailedDialog';
import { BlockSucceededDialog } from './BlockSucceededDialog';
import { FriendDetailDialog } from './detail/FriendDetailDialog';
import { OptionalBlockDialog } from './OptionalBlockDialog';
import { ReportCompleteDialog } from './ReportCompleteDialog';

const BestFriends: React.FC = () => {
  const { t } = useTranslation(['best_friends', 'translation']);
  const location = useLocation();
  const { friendItems, isFetching, isRefreshing, errorType } = useSelector(
    (state: RootState) => state.bestFriends
  );
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(actions.getFriendList());
  }, [dispatch]);

  const handleRefreshClick = useCallback(
    throttle(() => {
      dispatch(actions.updateFriendPresences());
    }, 3000),
    [dispatch]
  );

  const isEmpty = errorType || friendItems.length === 0;
  const showRefreshButton =
    !isFetching &&
    errorType !== 'loading-failed' &&
    errorType !== 'unstable-network' &&
    location.pathname === '/players/best-friends';

  return (
    <>
      <main
        className={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
          color: #4d3906;

          .body {
            flex: 1 1 auto;
            overflow-x: hidden;
            overflow-y: scroll;
            padding: 24px 0 0;
            pointer-events: ${location.pathname !== '/players/best-friends' ? 'none' : 'inherit'};
          }
          .refresh {
            position: fixed;
            bottom: 32px;
            right: 20px;
            opacity: 0;
            visibility: hidden;
            transform: scale(0.5);
            animation: 0.2s cubic-bezier(0.17, 0.89, 0.56, 1.72) ${reveal} both;
            animation-delay: 0.2s;
          }
        `}
      >
        <TitleHeader
          title={t('top')}
          hintPath={!isEmpty ? '/players/best-friends/hint' : undefined}
          hintLabel={!isEmpty ? t('aria_help') : undefined}
          backLabel={t('translation:aria_button_back_to_home')}
          color={'#ffd0ae'}
        >
          {!isEmpty && (
            <div
              className={css`
                background: #ffd0ae;
                p {
                  font-size: 12px;
                  width: 100%;
                  max-width: 480px;
                  margin: 0 auto;
                  padding: 8px 16px 12px;
                }
              `}
            >
              <p>{t('best_friends_desc')}</p>
            </div>
          )}
        </TitleHeader>
        <div className="body">
          {(() => {
            if (isFetching) {
              return <Loading />;
            } else if (errorType === 'loading-failed' || errorType === 'unstable-network') {
              return <LoadingFailed error={errorType} />;
            }
            if (friendItems.length === 0) {
              return <BestFriendsEmpty />;
            } else {
              return <BestFriendsContent friendItems={friendItems} />;
            }
          })()}
        </div>

        {showRefreshButton && (
          <div className="refresh">
            <RefreshButton
              {...{ isRefreshing, handleRefreshClick, ariaLabel: t('aria_button_refresh') }}
            />
          </div>
        )}
      </main>

      <Route path="/players/best-friends/report/:id">
        <BestFriendReportForm />
      </Route>
      <DialogRoute path="/players/best-friends/detail/:id">
        <FriendDetailDialog />
      </DialogRoute>
      <DialogRoute path="/players/best-friends/report-completed/:id">
        <ReportCompleteDialog />
      </DialogRoute>
      <DialogRoute path="/players/best-friends/optional-block/:id">
        <OptionalBlockDialog />
      </DialogRoute>
      <DialogRoute path="/players/best-friends/block-succeeded">
        <BlockSucceededDialog />
      </DialogRoute>
      <DialogRoute path="/players/best-friends/block-failed">
        <BlockFailedDialog />
      </DialogRoute>
      <DialogRoute path="/players/best-friends/hint">
        <BestFriendsHintDialog />
      </DialogRoute>
    </>
  );
};

export default BestFriends;
